import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useCallback,
  useEffect,
} from 'react'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { Source } from '../../Linkers.constants'
import {
  SharepointItem,
  SharepointFile,
  SharepointShareUrlObject,
} from './SharepointFilePickerModal.types'
import { useSharepointReceptorHelper } from './SharepointFilePickerModal.helpers'
import { useSharepointLinker } from './SharepointFilePickerModal.hooks'

interface OnSubmitProps {
  caption: string
  isViaRequest?: boolean
  relevantDate?: Timestamp
}

interface SharepointFilePickerContextProps {
  loading: boolean
  setLoading: (value: boolean) => void
  filesSelected: SharepointItem[]
  setFilesSelected: React.Dispatch<React.SetStateAction<SharepointItem[]>>
  onSubmit: (props: OnSubmitProps) => Promise<void>
  setSource: (source: Source) => void
  setSubjectIds: (subjectId: Array<string>) => void
  subjectIds: Array<string>
  shareUrlObject: SharepointShareUrlObject
  setShareUrlObject: React.Dispatch<
    React.SetStateAction<SharepointShareUrlObject>
  >
}

const SharepointFilePickerContext = createContext<
  SharepointFilePickerContextProps | undefined
>(undefined)

export const SharepointFilePickerProvider: React.FC<{
  children: ReactNode
}> = ({ children }) => {
  const [loading, setLoading] = useState(true)
  const [filesSelected, setFilesSelected] = useState<SharepointItem[]>([])
  const [source, setSource] = useState<Source>('Evidence')
  const [subjectIds, setSubjectIds] = useState<Array<string>>([])
  const [shareUrlObject, setShareUrlObject] =
    useState<SharepointShareUrlObject>({
      shareUrl: '',
      caption: '',
      isViaRequest: false,
    })
  const { receptor } = useSharepointReceptorHelper()
  const linkDocuments = useSharepointLinker()
  const onSubmit = useCallback(
    async ({ caption, relevantDate, isViaRequest = false }: OnSubmitProps) => {
      if (!receptor) return
      await linkDocuments(
        receptor.toObject(),
        (filesSelected || []).map((item: SharepointItem) => ({
          id: item.id,
          display: item.name,
          webUrl: item.webUrl,
          type: source,
          subjectIds: subjectIds,
          siteId: item.sharepointIds.siteId,
          relevantDate,
        })) as SharepointFile[],
        isViaRequest,
        caption,
      )
    },
    [filesSelected, subjectIds, linkDocuments, receptor, source],
  )
  useEffect(() => {
    if (filesSelected.length === 0) {
      setLoading(true)
    }
  }, [filesSelected])

  return (
    <SharepointFilePickerContext.Provider
      value={{
        loading,
        setLoading,
        filesSelected,
        setFilesSelected,
        onSubmit,
        setSource,
        setSubjectIds,
        subjectIds,
        shareUrlObject,
        setShareUrlObject,
      }}
    >
      {children}
    </SharepointFilePickerContext.Provider>
  )
}

export const useSharepointFilePickerContext =
  (): SharepointFilePickerContextProps => {
    const context = useContext(SharepointFilePickerContext)
    if (!context) {
      throw new Error(
        'useSharepointFilePicker must be used within a SharepointFilePickerProvider',
      )
    }
    return context
  }
